import React, { useState } from "react";
import {
    Dialog, DialogHeader, DialogBody, DialogFooter,
    Typography, Button, IconButton
} from "@material-tailwind/react";
import {
    EyeIcon,
    PlusCircleIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";

function parseGroupItemText(text) {
    const lines = text.split('\n').filter(line => line.trim());
    let orderNumber = '';
    const items = [];

    if (lines.length > 0) orderNumber = lines[0].trim();

    for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        const mangaMatch = line.match(/-\s*\[\s*]\s*(.+?)\s*-\s*£(\d+\.?\d*)\s*\((\w+)\)/);
        if (mangaMatch) {
            items.push({
                title: mangaMatch[1].trim(),
                price: parseFloat(mangaMatch[2]),
                itemCondition: mangaMatch[3].trim()
            });
        }
    }

    if (!orderNumber) throw new Error('Order number not found in first line');
    if (items.length === 0) throw new Error('No valid item entries found');

    return { orderNumber, items };
}

export default function GroupDialog({ isOpen, onClose, onSuccess, requestHeaders }) {

    const [groupTextInput, setGroupTextInput] = useState('');
    const [parsedGroupData, setParsedGroupData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const submitGroupData = async (groupData) => {
        try {
            setIsSubmitting(true);
            await axios.post(`${API_URL}/purchased_items/add_group`, groupData, { headers: requestHeaders });
            setGroupTextInput('');
            setParsedGroupData(null);
            onSuccess();
            onClose();
        } catch (err) {
            console.error("Error adding group items:", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePreview = () => {
        try {
            const data = parseGroupItemText(groupTextInput);
            setParsedGroupData(data);
        } catch (err) {
            alert(`Parse Error: ${err.message}`);
        }
    };

    const handleSubmit = async () => {
        if (!parsedGroupData || isSubmitting) return;
        await submitGroupData(parsedGroupData);
    };

    const handleClose = () => {
        setGroupTextInput('');
        setParsedGroupData(null);
        setIsSubmitting(false);
        onClose();
    };

    return (
        <Dialog size="l" open={isOpen} handler={handleClose}
                className="p-4 mt-10 mb-10 flex flex-col h-[80vh] max-h-[80vh] overflow-hidden">
            <DialogHeader className="relative m-0 block">
                <Typography variant="h4" color="blue-gray">
                    Add Item Group
                </Typography>
                <Typography className="mt-1 font-normal text-gray-600">
                    Paste your game group data. Every item is added with Type = Game and Format type = Game. Images are assigned automatically from matching watchlist titles.
                </Typography>
                <IconButton size="sm" variant="text" className="!absolute right-3.5 top-3.5" onClick={handleClose}>
                    <XMarkIcon className="h-4 w-4 stroke-2" />
                </IconButton>
            </DialogHeader>
            <DialogBody className="space-y-4 overflow-y-auto flex-grow max-h-[50vh] mb-4">
                <div>
                    <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold">Items Data</Typography>
                    <textarea
                        value={groupTextInput}
                        onChange={(e) => setGroupTextInput(e.target.value)}
                        placeholder={`WOB1197341728131\n24.10 - £19.49:\n- [ ] Spartan: Total Warrior (PS2) - £10.99 (vg)\n- [ ] God of War (PS2) - £8.50 (g)`}
                        className="w-full h-64 p-3 border border-gray-300 rounded-lg resize-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                        rows={10}
                    />
                </div>
                {parsedGroupData && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <Typography variant="h6" className="mb-2">Parsed Data Preview:</Typography>
                        <Typography variant="small">Order Number: {parsedGroupData.orderNumber}</Typography>
                        <Typography variant="small">Items: {parsedGroupData.items.length}</Typography>
                        <Typography variant="small">Type: Game</Typography>
                        <Typography variant="small">Format type: Game</Typography>
                    </div>
                )}
            </DialogBody>
            <DialogFooter className="flex items-center justify-between gap-2">
                <Button onClick={handlePreview} className="mv-secondary-button" disabled={isSubmitting}>
                    <EyeIcon className="h-4 w-4" />
                    Preview
                </Button>
                <Button onClick={handleSubmit} disabled={!parsedGroupData || isSubmitting} className="mv-primary-button">
                    <PlusCircleIcon className="h-4 w-4" />
                    {isSubmitting ? 'Adding...' : `Add ${parsedGroupData ? `${parsedGroupData.items.length} Items` : 'Items'}`}
                </Button>
            </DialogFooter>
        </Dialog>
    );
}
